/**
 * Sub-lines — the report's "By sub-line" table.
 *
 * A sub-line is the CLIENT'S plan line — "Core 500ml", "1L Growth", "Zero
 * Range", "Plus Range" — not the warehouse's l2 category. This file used to
 * say they were the same thing and the server grouped accordingly, which put
 * two rows on the page ("Hydration Drinks", "Energy Drinks") where the plan
 * has four. They are not even nested: the warehouse files two 1L Growth SKUs
 * under Energy Drinks and a Core 500ml one under Hydration Drinks.
 *
 * The server now groups on the plan's own sku_name membership and says which
 * grain it used, since a tenant with no plan lines defined still gets the l2
 * view.
 */
import type { ReactNode } from "react";
import { Delta, MetricCell, RowLabel, TableCard, fmt } from "../ds";
import { ErrorBox, State } from "../Panels";
import { useReport } from "../useReport";
import { SubLineCityDrill } from "./CitiesCard";
import {
  type Basis,
  type DrillRow,
  type OpsWindow,
  bandColor,
  basisLabel,
  canCompare,
  comparisonLabel,
  daysBetween,
  num,
  periodLabel,
  str,
  sumOf,
  useDrill,
} from "./opsLib";
import type { DateRange } from "@/lib/dateRange";

interface SubLineRow {
  name: string;
  gmv: number | null;
  prevGmv: number | null;
  share: number | null;
  units: number | null;
  drr: number | null;
  osa: number | null;
  asp: number | null;
  discount: number | null;
  skus: number | null;
  crawlDays: number | null;
  fillRate: number | null;
}

export function subLineRowsOf(
  rows: Array<Record<string, unknown>>,
  basis: Basis,
): SubLineRow[] {
  const mrpTotal = sumOf(rows, (r) => num(r["gmv_mrp"])) ?? 0;
  const spTotal = sumOf(rows, (r) => num(r["gmv"])) ?? 0;
  const total = basis === "mrp" ? mrpTotal : spTotal;
  return rows
    .map((r) => {
      const gmv = basis === "mrp" ? num(r["gmv_mrp"]) : num(r["gmv"]);
      return {
        name: str(r["sub_line"]) || "—",
        gmv,
        // The API's previous-period total for a sub-line is on MRP only, so the
        // comparison exists on MRP and honestly does not on SP.
        prevGmv: basis === "mrp" ? num(r["prev_gmv_mrp"]) : null,
        // share_pct from the server is the MRP share. On SP it is re-derived from
        // the rows on screen rather than reused, which would be a share of a
        // different denominator wearing this basis's label.
        share:
          basis === "mrp"
            ? num(r["share_pct"])
            : total
              ? ((gmv ?? 0) / total) * 100
              : null,
        units: num(r["units"]),
        drr: num(r["drr"]),
        osa: num(r["osa"]),
        asp: num(r["asp"]),
        discount: num(r["discount_pct"]),
        skus: num(r["skus"]),
        crawlDays: num(r["crawl_days"]),
        fillRate: num(r["fill_rate"]),
      };
    })
    .sort((a, b) => (b.gmv ?? 0) - (a.gmv ?? 0));
}

export function SubLinesCard({
  customer,
  range,
  basis,
}: {
  customer: string;
  range: DateRange;
  basis: Basis;
}) {
  const q = useReport("/v1/reports/ops/sub-lines", customer, range);
  const { push } = useDrill();

  if (q.isLoading) return <State>Loading sub-lines…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;

  const w: OpsWindow | undefined = q.data?.window;
  const scope = periodLabel(w?.start, w?.end);
  const raw = q.data?.rows ?? [];
  if (raw.length === 0) {
    return (
      <section className="card">
        <div className="card-head">
          <span className="card-title">By sub-line</span>
        </div>
        <div className="empty">No crawl days in {scope}.</div>
      </section>
    );
  }

  const rows = subLineRowsOf(raw, basis);
  const days = w ? daysBetween(w.start, w.end) : null;
  // The server states the grain rather than leaving the page to assume one.
  const planGrain = q.data?.subline_basis !== "l2_category_name";

  /*
   * "10 crawl days · 8 SKUs", as the published report prints it.
   *
   * The old sub-label was the window length, which every row shared and which
   * the date pill above already states. What a reader cannot otherwise tell is
   * how much is behind a line: eight listings or two, and how many days the
   * crawl actually saw.
   */
  const sub = (r: SubLineRow): string | undefined => {
    const parts: string[] = [];
    const d = r.crawlDays ?? days;
    if (d !== null) parts.push(`${d} crawl day${d === 1 ? "" : "s"}`);
    if (r.skus !== null) parts.push(`${r.skus} SKU${r.skus === 1 ? "" : "s"}`);
    return parts.length ? parts.join(" · ") : undefined;
  };

  const cells = (r: SubLineRow): ReactNode[] => [
    <RowLabel key="s" name={r.name} sub={sub(r)} />,
    <MetricCell key="g" value={fmt(r.gmv, "money")} />,
    <Delta key="d" cur={r.gmv} prev={r.prevGmv} good="up" />,
    r.share === null ? "—" : `${r.share.toFixed(1)}%`,
    fmt(r.units, "int"),
    fmt(r.drr, "int"),
    <MetricCell
      key="o"
      value={
        <span style={{ color: bandColor(r.osa) }}>{fmt(r.osa, "pct")}</span>
      }
    />,
    fmt(r.asp, "price"),
    fmt(r.discount, "pct"),
    r.fillRate !== null ? (
      <span key="fr" style={{ color: bandColor(r.fillRate) }}>
        {fmt(r.fillRate, "pct")}
      </span>
    ) : (
      "—"
    ),
  ];

  return (
    <TableCard
      title="By sub-line"
      scope={scope}
      asof={`Data to ${w?.as_of ?? "—"}`}
      cols={[
        { label: "Sub-line" },
        {
          label: `GMV (${basisLabel(basis)})`,
          type: "num",
          title:
            basis === "mrp"
              ? "SUM(COALESCE(mrp_sales_actual, final_est_sales_mrp)) — at MRP, the basis the plan is written on"
              : "SUM(COALESCE(sp_sales_actual, final_est_sales_sp)) — the selling price actually realised",
        },
        { label: `vs ${comparisonLabel(w)}`, type: "num" },
        { label: "Share of GMV", type: "num" },
        { label: "Units", type: "num" },
        {
          label: "DRR (units/day)",
          type: "num",
          title: "SUM(units) / CALENDAR days in the window — not days in stock",
        },
        {
          label: "OSA %",
          type: "num",
          title:
            "Sales-weighted: SUM(osa_absolute × weight) / SUM(weight), pooled over the window",
        },
        {
          label: "ASP",
          type: "num",
          title:
            "SUM(final_est_sales_sp) / SUM(final_est_units) — a selling price, always SP, on one basis both sides of the ratio",
        },
        {
          label: "Discount %",
          type: "num",
          title: "1 − SUM(final_est_sales_sp) / SUM(final_est_sales_mrp)",
        },
        {
          label: "Fill rate",
          type: "num",
          title:
            "SUM(delivered) / SUM(ordered) on CLOSED POs for this sub-line's item_ids",
        },
      ]}
      rows={rows.map((r): DrillRow => ({
        key: r.name,
        cls: "clickable",
        onClick: () => {
          push(
            <SubLineCityDrill
              customer={customer}
              range={range}
              basis={basis}
              subLine={r.name}
            />,
          );
        },
        cells: cells(r),
      }))}
      foot={
        <>
          {planGrain ? (
            <>
              The client&rsquo;s ranges as the plan file defines them &mdash;
              membership is by listing, from the plan&rsquo;s own sub-line map,{" "}
              <b>not</b> the warehouse&rsquo;s <code>l2_category_name</code>.
              The two cross rather than nest: two 1L Growth listings are filed
              under Energy Drinks and a Core 500ml one under Hydration Drinks,
              so a category view can never roll up to these lines.{" "}
            </>
          ) : (
            <>
              <b>These are warehouse categories, not plan lines.</b> No sub-line
              membership is defined for this brand, so the table falls back to{" "}
              <code>l2_category_name</code> &mdash; the rows are a shelf view
              and will not line up with a plan.{" "}
            </>
          )}
          Every figure pools from the same rollup as the city table, so the
          sub-lines add to the same crawl total the Cities page does.{" "}
          <b>They will not tie to Plan &amp; forecast</b>, which measures the
          same four lines against the brand&rsquo;s portal download &mdash; all
          listings, combo volume included, ungated by crawl coverage &mdash; and
          reads higher for that reason. <b>ASP is always a selling price</b> and
          does not move with the MRP/SP toggle; the toggle changes GMV, which is
          what the plan is set on, and discount is the gap between the two.{" "}
          <b>OSA here is sales-weighted</b> &mdash; each SKU counts in
          proportion to what it sells &mdash; while the Cities page shows the
          unweighted shelf, so the two answer different questions and are not
          expected to match.{" "}
          {basis === "sp" ? (
            <>
              The comparison is blank on the SP basis because the API returns a
              previous-period sub-line total at MRP only; switch to MRP to read
              the change.{" "}
            </>
          ) : (
            !canCompare(w) && (
              <>
                No comparison is shown because the previous window falls outside
                the history this source holds.{" "}
              </>
            )
          )}
          <b>Click a sub-line for its cities.</b>
        </>
      }
    />
  );
}
